import React, { useState, useContext, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import AnalitycsCardContent from './components/AnalitycsCardContent';
import { Button, Loader } from 'ncoded-component-library';
import { TFunction, useTranslation } from 'react-i18next';
import dataRoomApi from '../../dataRoomApi';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { AnaliticsResponse } from '../../dataRoomApi/questions-and-answers/sell';
import ReportsEmptyState from '../../subrouters/Reports/pages/Reports/components/ReportsEmptyState';

import './QAndAChart.styles.scss';
import './QAndAChart.styles.responsive.scss';

type QAndAChartProps = {
  className?: string;
};

type TilesType = {
  text: string;
  secondaryText: string;
};

type ChartValueType = {
  text: string;
  count: number;
};

export type StagesStats = {
  tiles: Array<TilesType>;
  chartValues: Array<ChartValueType>;
  min: number;
  max: number;
};

const DEFAULT_MIN = 0;
const DEFAULT_MAX = 10;

const SUFIX_MAP: Partial<Record<keyof AnaliticsResponse, string>> = {
  totalPercentOfPublishedQuestions: '%',
  totalAverageMinutesToPublish: 'd',
};

const createStats = (
  data: AnaliticsResponse,
  t: TFunction<'translation', undefined>,
) =>
  Object.entries(data).reduce((acc, [key, val]) => {
    if (key !== 'graph') {
      if (key === 'totalNumberOfSubjects') return acc;
      return {
        ...acc,
        tiles: [
          ...(acc.tiles ? (acc as any).tiles : []),
          {
            text: `${val}
             ${
               SUFIX_MAP[key as keyof AnaliticsResponse]
                 ? SUFIX_MAP[key as keyof AnaliticsResponse]
                 : ''
             }`,
            secondaryText: t(key, {
              count: (data as any).totalNumberOfSubjects,
            }),
          },
        ],
      };
    } else {
      return {
        ...acc,
        min: (val as any).min || DEFAULT_MIN,
        max: (val as any).max || DEFAULT_MAX,
        chartValues: ((val as any).data as any).map(({ key, value }: any) => ({
          text: key,
          count: value,
        })),
      };
    }
  }, {} as StagesStats);

const QAndAChart: React.FC<QAndAChartProps> = (props) => {
  const { className } = props;
  const { t } = useTranslation();
  const [isSubjectShown, setIsSubjectShown] = useState(false);
  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);
  const { popServerError } = useContext(PopNotificationsContext);
  const [stageStats, setStageStats] = useState<StagesStats>();
  const [subjectStats, setSubjectStats] = useState<StagesStats>();
  const [isDataLoading, setIsDataLoading] = useState(true);

  const classes = classNames('q-and-a-chart', className);

  useEffect(() => {
    (async () => {
      try {
        setIsDataLoading(true);

        const { data } =
          await dataRoomApi.questionsAndAnswers.getAnaliticsByStage(dataRoomId);

        const createdStats = createStats(data, t);
        setStageStats(createdStats);

        const { data: subjectData } =
          await dataRoomApi.questionsAndAnswers.getAnaliticsBySubject(
            dataRoomId,
          );

        const createdSubjectStats = createStats(subjectData, t);

        setSubjectStats(createdSubjectStats);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsDataLoading(false);
      }
    })();
  }, [dataRoomId, popServerError, t]);

  const emptyStage = useMemo(
    () => !stageStats || stageStats.chartValues.every((el) => !el.count),
    [stageStats],
  );
  const emptySubject = useMemo(
    () => !subjectStats || subjectStats.chartValues.every((el) => !el.count),
    [subjectStats],
  );

  return (
    <div className={classes}>
      <div className="q-and-a-chart__heading">
        <p>{t(isSubjectShown ? 'activityBySubject' : 'activityOverall')}</p>
        <div className="button-container">
          <Button
            className={classNames({ selected: !isSubjectShown })}
            onClick={() => setIsSubjectShown(false)}
          >
            {t('stage')}
          </Button>
          <Button
            className={classNames({ selected: isSubjectShown })}
            onClick={() => setIsSubjectShown(true)}
          >
            {t('subject')}
          </Button>
        </div>
      </div>

      {isDataLoading ? (
        <Loader />
      ) : (
        <div
          className={classNames('q-and-a-chart__content', {
            translate: isSubjectShown,
          })}
        >
          {emptyStage ? (
            <ReportsEmptyState />
          ) : (
            <AnalitycsCardContent chartAndTileStats={stageStats} />
          )}
          {emptySubject ? (
            <ReportsEmptyState />
          ) : (
            <AnalitycsCardContent chartAndTileStats={subjectStats} />
          )}
        </div>
      )}
    </div>
  );
};

export default QAndAChart;
